/**
 * Encounter segmentation: each mission encounter is recorded as a separate
 * telemetry segment with its own cue/decision/clearance metrics. Only the
 * controlled pedestrian encounter is research-labelled; everything else is
 * exploratory/gameplay data (ADR-019). Pure module — no DOM, no Babylon.
 */

import type { AgentModel } from "./agents";
import { COMMIT_OFFSET, LATERAL_INPUT_THRESHOLD } from "./constants";
import type { RoadType } from "./network";
import type { Side } from "./types";

export interface EncounterConfig {
  id: string;
  type: string;
  section: number;
  /** Zone bounds in delivery-path progress s (m). */
  zoneStart: number;
  zoneEnd: number;
  research: boolean;
  agentIds: string[];
  /** Road type the zone lies on (realism rules, ADR-022). */
  roadType: RoadType;
}

/** One recorded encounter segment (episode JSON `encounters[]`). */
export interface EncounterRecord {
  encounter_id: string;
  encounter_type: string;
  section: number;
  road_type: RoadType;
  start_time: number | null;
  cue_onset_time: number | null;
  research_window_start: number | null;
  research_window_end: number | null;
  first_lateral_input: number | null;
  first_commitment: number | null;
  commitment_direction: Side | null;
  yield_event: number | null;
  direction_reversals: number;
  minimum_clearance: number | null;
  safety_slowdowns: number;
  emergency_stops: number;
  recovery_events: number;
  encounter_completion: number | null;
  encounter_valid_for_research: boolean;
}

export interface EncounterStepInput {
  t: number;
  robotX: number;
  robotZ: number;
  /** Delivery-path progress (m). */
  robotS: number;
  robotSpeed: number;
  /** Lateral offset from the delivery path (m, +right). */
  robotOffset: number;
  engaged: boolean;
  joyX: number;
  cruise: number;
  /** True while an automatic recovery maneuver controls the robot — player
   * decisions must never latch from automated poses. */
  recovering: boolean;
}

function freshRecord(cfg: EncounterConfig): EncounterRecord {
  return {
    encounter_id: cfg.id,
    encounter_type: cfg.type,
    section: cfg.section,
    road_type: cfg.roadType,
    start_time: null,
    cue_onset_time: null,
    research_window_start: null,
    research_window_end: null,
    first_lateral_input: null,
    first_commitment: null,
    commitment_direction: null,
    yield_event: null,
    direction_reversals: 0,
    minimum_clearance: null,
    safety_slowdowns: 0,
    emergency_stops: 0,
    recovery_events: 0,
    encounter_completion: null,
    encounter_valid_for_research: false,
  };
}

const YIELD_AGENT_DIST = 5.0;
const YIELD_HOLD_S = 0.5;

export class EncounterManager {
  readonly configs: EncounterConfig[];
  private readonly records = new Map<string, EncounterRecord>();
  private readonly agents: Map<string, AgentModel>;
  private readonly robotHalfWidth: number;
  private activeId: string | null = null;
  private lastCommitSide: Side | null = null;
  private yieldHold = 0;
  /** Commitment arms only after the robot has been inside the neutral band
   * (|offset| < COMMIT_OFFSET) post-cue — otherwise lateral offset carried in
   * from a route curve would stamp a phantom "decision" at zone entry.
   * (Research encounter only — its zone is straight, matching the lab.) */
  private commitArmed = false;
  /** Non-research encounters measure commitment from sustained lateral INPUT
   * (curve-proof: route-centerline offset is geometry, not decision). */
  private lateralHoldS = 0;
  private lateralSign: Side | null = null;

  /** Events for the sim to emit this step. */
  pending: Array<{ type: "encounter_start" | "encounter_end"; data: Record<string, unknown> }> =
    [];

  constructor(configs: EncounterConfig[], agents: AgentModel[], robotHalfWidth = 0.3) {
    this.configs = configs;
    this.robotHalfWidth = robotHalfWidth;
    this.agents = new Map(agents.map((a) => [a.state.id, a]));
    for (const c of configs) this.records.set(c.id, freshRecord(c));
  }

  get active(): EncounterConfig | null {
    return this.configs.find((c) => c.id === this.activeId) ?? null;
  }

  record(id: string): EncounterRecord | null {
    return this.records.get(id) ?? null;
  }

  /** The active encounter's decision (for agent reactions), or null. */
  decisionFor(agentId: string): Side | "yield" | null {
    const cfg = this.configs.find((c) => c.agentIds.includes(agentId));
    if (!cfg) return null;
    const r = this.records.get(cfg.id)!;
    if (r.commitment_direction) return r.commitment_direction;
    if (r.yield_event !== null) return "yield";
    return null;
  }

  agentsOf(cfg: EncounterConfig): AgentModel[] {
    return cfg.agentIds
      .map((id) => this.agents.get(id))
      .filter((a): a is AgentModel => !!a);
  }

  /** Close the active encounter (the run ended inside its zone). */
  closeActive(t: number): void {
    if (!this.activeId) return;
    const cfg = this.active!;
    const r = this.records.get(cfg.id)!;
    r.encounter_completion = t;
    this.pending.push({ type: "encounter_end", data: { encounter_id: cfg.id, encounter_type: cfg.type } });
    this.activeId = null;
  }

  /** Advance segmentation; call once per sim step AFTER agents stepped. */
  step(input: EncounterStepInput, dt: number): void {
    // Transition: complete the active encounter when the robot leaves its zone.
    if (this.activeId) {
      const cfg = this.active!;
      const r = this.records.get(cfg.id)!;
      if (input.robotS > cfg.zoneEnd) {
        r.encounter_completion = input.t;
        this.pending.push({
          type: "encounter_end",
          data: { encounter_id: cfg.id, encounter_type: cfg.type },
        });
        this.activeId = null;
        this.lastCommitSide = null;
        this.yieldHold = 0;
        this.commitArmed = false;
        this.lateralHoldS = 0;
        this.lateralSign = null;
      }
    }
    // Activate the encounter whose zone the robot entered.
    if (!this.activeId) {
      const next = this.configs.find(
        (c) =>
          input.robotS >= c.zoneStart &&
          input.robotS <= c.zoneEnd &&
          this.records.get(c.id)!.encounter_completion === null,
      );
      if (next) {
        this.activeId = next.id;
        this.commitArmed = false;
        this.lateralHoldS = 0;
        this.lateralSign = null;
        const r = this.records.get(next.id)!;
        if (r.start_time === null) {
          r.start_time = input.t;
          this.pending.push({
            type: "encounter_start",
            data: { encounter_id: next.id, encounter_type: next.type, section: next.section },
          });
        }
      }
    }
    const cfg = this.active;
    if (!cfg) return;
    const r = this.records.get(cfg.id)!;

    // Cue onset: first agent cue phase > 0. The research encounter's cue is
    // the DRIFT onset, stamped by the sim via setResearchWindow — the
    // pedestrian's mere visible approach does not count as its cue.
    if (r.cue_onset_time === null && !cfg.research) {
      for (const a of this.agentsOf(cfg)) {
        if (a.state.cuePhase > 0) {
          r.cue_onset_time = input.t;
          break;
        }
      }
    }

    // Player-behavior metrics within the zone. Suspended entirely while an
    // automatic recovery drives the robot — automation must never latch or
    // flip a player decision (same invariant as the sim-level latch).
    if (!input.recovering) {
      if (
        r.first_lateral_input === null &&
        input.engaged &&
        Math.abs(input.joyX) >= LATERAL_INPUT_THRESHOLD
      ) {
        r.first_lateral_input = input.t;
      }

      if (cfg.research) {
        // Research: lab-identical geometric latch (the zone is straight), but
        // armed only once the robot has been INSIDE the neutral band in-zone —
        // offset carried in from the warmup is not a decision.
        if (!this.commitArmed && Math.abs(input.robotOffset) < COMMIT_OFFSET) {
          this.commitArmed = true;
        }
        if (this.commitArmed && Math.abs(input.robotOffset) >= COMMIT_OFFSET && input.engaged) {
          const side: Side = input.robotOffset < 0 ? "left" : "right";
          if (r.first_commitment === null) {
            r.first_commitment = input.t;
            r.commitment_direction = side;
            this.lastCommitSide = side;
          } else if (this.lastCommitSide !== null && this.lastCommitSide !== side) {
            r.direction_reversals += 1;
            this.lastCommitSide = side;
          }
        }
      } else if (r.cue_onset_time !== null && input.engaged) {
        // Exploratory encounters: commitment = sustained lateral INPUT after
        // the cue (0.3 s of |joyX| >= threshold). Centerline offset is
        // geometry — on curves it moves without any player decision.
        if (Math.abs(input.joyX) >= LATERAL_INPUT_THRESHOLD) {
          const side: Side = input.joyX < 0 ? "left" : "right";
          if (this.lateralSign === side) this.lateralHoldS += dt;
          else {
            this.lateralSign = side;
            this.lateralHoldS = dt;
          }
          if (this.lateralHoldS >= 0.3) {
            if (r.first_commitment === null) {
              r.first_commitment = input.t;
              r.commitment_direction = side;
              this.lastCommitSide = side;
            } else if (this.lastCommitSide !== null && this.lastCommitSide !== side) {
              r.direction_reversals += 1;
              this.lastCommitSide = side;
            }
          }
        } else {
          this.lateralHoldS = 0;
          this.lateralSign = null;
        }
      }
    }

    // Yield: sustained release or near-stop while an encounter agent is near
    // (also suspended during recovery — a reverse creep is not a yield).
    const nearest = this.nearestAgentDist(cfg, input.robotX, input.robotZ);
    if (input.recovering) {
      this.yieldHold = 0;
    } else if (r.yield_event === null && nearest !== null && nearest.gap < YIELD_AGENT_DIST) {
      const yielding = !input.engaged || input.robotSpeed < 0.15 * input.cruise;
      this.yieldHold = yielding ? this.yieldHold + dt : 0;
      if (this.yieldHold >= YIELD_HOLD_S) r.yield_event = input.t;
    }

    // Minimum clearance to this encounter's collidable agents.
    if (nearest !== null) {
      r.minimum_clearance =
        r.minimum_clearance === null
          ? nearest.clearance
          : Math.min(r.minimum_clearance, nearest.clearance);
    }
  }

  private nearestAgentDist(
    cfg: EncounterConfig,
    x: number,
    z: number,
  ): { gap: number; clearance: number } | null {
    let best: { gap: number; clearance: number } | null = null;
    for (const a of this.agentsOf(cfg)) {
      const s = a.state;
      if (!s.active || !s.collidable) continue;
      const gap = Math.hypot(s.x - x, s.z - z);
      const clearance = gap - s.radius - this.robotHalfWidth;
      if (!best || gap < best.gap) best = { gap, clearance };
    }
    return best;
  }

  /** Bump a counter on the active encounter (safety/recovery bookkeeping). */
  bump(
    field: "safety_slowdowns" | "emergency_stops" | "recovery_events",
  ): string | null {
    if (!this.activeId) return null;
    const r = this.records.get(this.activeId)!;
    r[field] += 1;
    return this.activeId;
  }

  /** Research window bookkeeping (research encounter only). The drift onset
   * doubles as the encounter's cue_onset_time. */
  setResearchWindow(id: string, start: number | null, end: number | null): void {
    const r = this.records.get(id);
    if (!r) return;
    if (start !== null && r.research_window_start === null) {
      r.research_window_start = start;
      if (r.cue_onset_time === null) r.cue_onset_time = start;
    }
    // A window can only end if it actually opened.
    if (end !== null && r.research_window_start !== null && r.research_window_end === null) {
      r.research_window_end = end;
    }
  }

  finalize(researchSampleValid: boolean): EncounterRecord[] {
    for (const c of this.configs) {
      const r = this.records.get(c.id)!;
      // Research-valid requires the stimulus to have actually been delivered
      // (cue fired), on top of episode validity and zero recoveries.
      r.encounter_valid_for_research =
        c.research &&
        researchSampleValid &&
        r.recovery_events === 0 &&
        r.cue_onset_time !== null;
    }
    return this.configs.map((c) => this.records.get(c.id)!);
  }
}
